import * as React from "react"
import * as Yup from "yup"
import { Form, FormElement } from "@modules/form/components/Form"
import {
  Button,
  StyledModalActions,
  TextAreaConnectedField,
  useShowSuccessToast,
} from "gatsby-interface"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.json"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"

import {
  HostingDetailsForSiteDocument,
  useUpsertCloudCdnCertificateMutation,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

type FormValues = {
  tlsKey: string
  tlsCert: string
  tlsIntermediateCerts?: string
}

const validationSchema = Yup.object().shape<FormValues>({
  tlsKey: Yup.string().required(uiText.messages.validationIsRequired),
  tlsCert: Yup.string().required(uiText.messages.validationIsRequired),
  tlsIntermediateCerts: Yup.string().required(
    uiText.messages.validationIsRequired
  ),
})

export type CustomCertificateFormProps = {
  siteId: string
  onComplete: () => void
  onCancel: () => void
}

export function CustomCertificateForm({
  siteId,
  onComplete,
  onCancel,
}: CustomCertificateFormProps) {
  const [
    upsertCloudCdnCertificateMutation,
  ] = useUpsertCloudCdnCertificateMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()

  return (
    <Form<FormValues>
      initialValues={{
        tlsKey: ``,
        tlsCert: ``,
        tlsIntermediateCerts: ``,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        const { tlsKey, tlsCert, tlsIntermediateCerts } = values

        return upsertCloudCdnCertificateMutation({
          variables: {
            siteId,
            tlsKey,
            tlsCert,
            tlsIntermediateCerts,
          },
          refetchQueries: [
            {
              query: HostingDetailsForSiteDocument,
              variables: { siteId },
            },
          ],
        })
          .then(({ data }) => {
            const success = data?.upsertCloudCdnCertificate?.success ?? false

            if (success) {
              onComplete()
              return showSuccessToast(text.messages.sslCertificateAdded)
            }

            const message =
              data?.upsertCloudCdnCertificate?.message ||
              errorText.messages.somethingWentWrong
            setError({ message })
          })
          .catch(err => {
            return setError(err)
          })
      }}
    >
      {({ isSubmitting }) => {
        return (
          <FormElement applySpacing noValidate>
            <TextAreaConnectedField
              label={text.labels.privateKey}
              name="tlsKey"
              placeholder={text.messages.placeYourPrivateKey}
              required
            />
            <TextAreaConnectedField
              label={text.labels.certificate}
              name="tlsCert"
              placeholder={text.messages.placeYourPemFormatedCertificate}
              required
            />
            <TextAreaConnectedField
              label={text.labels.intermediateCertificates}
              name="tlsIntermediateCerts"
              placeholder={text.messages.placeYourCaCertificateChain}
              required
            />
            {errorAlert}
            <StyledModalActions>
              <Button variant="SECONDARY" type="reset" onClick={onCancel}>
                {uiText.actions.cancel}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingLabel={uiText.messages.saving}
              >
                {uiText.actions.save}
              </Button>
            </StyledModalActions>
          </FormElement>
        )
      }}
    </Form>
  )
}
